// CameraToolOverlay — DOM-overlay поверх Canvas для активных режимов
// pan / orbit (включаются кнопками SketchCameraToolbar).
//
// Когда `cameraTool` в store === 'pan' или 'orbit' — мы перекрываем сцену
// невидимым div'ом, ловим pointer-down/move/up и дёргаем
// window.__kubikonCadCamera.{panByPixels,orbitByPixels}.
//
// Esc или повторный клик кнопки — выходит из режима (cameraTool=null).

import React, { useEffect, useRef } from 'react';
import useCadStore from '../store/useCadStore';
import cl from './CameraToolOverlay.module.css';


const CameraToolOverlay = () => {
    const cameraTool = useCadStore((s) => s.cameraTool);
    const setCameraTool = useCadStore((s) => s.setCameraTool);
    const dragging = useRef(false);
    const lastPos = useRef({ x: 0, y: 0 });

    // Esc — выйти из режима
    useEffect(() => {
        if (!cameraTool) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setCameraTool(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [cameraTool, setCameraTool]);

    // Только pan-режим перехватывает всю сцену.
    // В orbit-режиме событиям нужен fall-through к OrbitGizmo (круги внутри Canvas).
    if (cameraTool !== 'pan') return null;

    const callApi = (method, ...args) => {
        const api = window.__kubikonCadCamera;
        if (!api || typeof api[method] !== 'function') return;
        try { api[method](...args); } catch (_) {}
    };

    const onDown = (e) => {
        if (e.button !== 0) return; // только ЛКМ
        e.preventDefault();
        dragging.current = true;
        lastPos.current = { x: e.clientX, y: e.clientY };
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (_) {}
    };

    const onMove = (e) => {
        if (!dragging.current) return;
        const dx = e.clientX - lastPos.current.x;
        const dy = e.clientY - lastPos.current.y;
        lastPos.current = { x: e.clientX, y: e.clientY };
        callApi('panByPixels', dx, dy);
    };

    const onUp = (e) => {
        if (!dragging.current) return;
        dragging.current = false;
        try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch (_) {}
    };

    // Колесо мыши — зум, даже когда tool активен (привычное поведение)
    const onWheel = (e) => {
        e.preventDefault();
        if (e.deltaY > 0) callApi('zoomOut');
        else              callApi('zoomIn');
    };

    const cursor = cameraTool === 'pan'
        ? (dragging.current ? 'grabbing' : 'grab')
        : (dragging.current ? 'grabbing' : 'move');

    return (
        <div
            className={cl.overlay}
            style={{ cursor }}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            onWheel={onWheel}
        />
    );
};

export default CameraToolOverlay;
